import { useState } from "react";
import { Monitor, Smartphone, Eye, EyeOff, ChevronUp, ChevronDown, RotateCcw, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { sorteerVoorMobiel } from "@/lib/weergave";

const WEERGAVEN = [
  ["desktop", Monitor, "Desktop"],
  ["mobiel", Smartphone, "Mobiel"],
];

/* Schakelaar desktop / mobiel (indeling bewerken of voorbeeld bekijken). */
export const WeergaveTabs = ({ value, onChange, testIdPrefix = "weergave" }) => (
  <div className="inline-flex rounded-full border bg-background p-1" role="tablist">
    {WEERGAVEN.map(([w, Icon, label]) => (
      <button
        key={w}
        type="button"
        role="tab"
        aria-selected={value === w}
        onClick={() => onChange(w)}
        className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${value === w ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
        data-testid={`${testIdPrefix}-${w}`}
      >
        <Icon className="h-4 w-4" aria-hidden="true" />
        {label}
      </button>
    ))}
  </div>
);

/* Volgorde + zichtbaarheid van bestaande blokken op mobiel; inhoud wordt in de desktopweergave bewerkt. */
export const MobielIndeling = ({ blokken, mobiel, labelVan, onChange, testIdPrefix = "mobiel" }) => {
  const lijst = sorteerVoorMobiel(blokken, mobiel);
  const verborgen = new Set(mobiel?.verborgen || []);
  const zet = (volgorde, verb) => onChange({ volgorde: volgorde.map((b) => b.id), verborgen: [...verb] });
  const verplaats = (i, richting) => {
    const j = i + richting;
    if (j < 0 || j >= lijst.length) return;
    const next = [...lijst];
    [next[i], next[j]] = [next[j], next[i]];
    zet(next, verborgen);
  };
  const toggle = (id) => {
    const next = new Set(verborgen);
    next.has(id) ? next.delete(id) : next.add(id);
    zet(lijst, next);
  };

  return (
    <Card className="p-5" data-testid={`${testIdPrefix}-indeling`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-base font-semibold">Indeling op mobiel</h3>
          <p className="mt-1 max-w-xl text-xs text-muted-foreground">
            Op een telefoon staan alle blokken onder elkaar. Bepaal hier de volgorde en verberg blokken die op een klein
            scherm niet nodig zijn. De inhoud van een blok bewerk je in de desktopweergave; die is voor beide gelijk.
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => onChange(null)} disabled={!mobiel} data-testid={`${testIdPrefix}-reset`}>
          <RotateCcw className="mr-2 h-4 w-4" />Zelfde als desktop
        </Button>
      </div>
      {!mobiel && (
        <p className="mt-3 rounded-md bg-secondary px-3 py-2 text-xs text-muted-foreground" data-testid={`${testIdPrefix}-volgt-desktop`}>
          Mobiel volgt nu de desktopindeling. Zodra je hier iets verschuift of verbergt, krijgt mobiel een eigen indeling.
        </p>
      )}
      <ol className="mt-4 space-y-2">
        {lijst.map((b, i) => {
          const weg = verborgen.has(b.id);
          return (
            <li
              key={b.id}
              className={`flex items-center gap-2 rounded-lg border bg-card px-3 py-2 ${weg ? "opacity-50" : ""}`}
              data-testid={`${testIdPrefix}-blok-${b.id}`}
            >
              <span className="w-6 font-mono text-[10px] text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
              <span className="flex-1 truncate text-sm font-medium">{labelVan(b)}</span>
              {weg && <span className="text-[10px] uppercase tracking-wide text-muted-foreground">verborgen op mobiel</span>}
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => verplaats(i, -1)} disabled={i === 0} aria-label="Omhoog" data-testid={`${testIdPrefix}-up-${i}`}>
                <ChevronUp className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => verplaats(i, 1)} disabled={i === lijst.length - 1} aria-label="Omlaag" data-testid={`${testIdPrefix}-down-${i}`}>
                <ChevronDown className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => toggle(b.id)} aria-label={weg ? "Tonen op mobiel" : "Verbergen op mobiel"} data-testid={`${testIdPrefix}-toggle-${i}`}>
                {weg ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </li>
          );
        })}
      </ol>
    </Card>
  );
};

/* Voorbeeld van de echte pagina in een iframe, schakelbaar tussen desktop en telefoon. */
export const VoorbeeldDialog = ({ open, onOpenChange, pad, titel, melding }) => {
  const [weergave, setWeergave] = useState("desktop");
  const url = pad ? `${window.location.origin}${pad}` : null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[94vh] max-w-6xl overflow-hidden p-0" data-testid="voorbeeld-dialog">
        <DialogHeader className="flex-row items-center justify-between gap-3 space-y-0 border-b px-5 py-3 pr-12 text-left">
          <div>
            <DialogTitle className="text-base">{titel}</DialogTitle>
            <DialogDescription className="text-xs">{melding}</DialogDescription>
          </div>
          <div className="flex items-center gap-2">
            <WeergaveTabs value={weergave} onChange={setWeergave} testIdPrefix="voorbeeld" />
            {url && (
              <Button variant="ghost" size="icon" asChild aria-label="Open in nieuw tabblad">
                <a href={url} target="_blank" rel="noreferrer" data-testid="voorbeeld-open-tab"><ExternalLink className="h-4 w-4" /></a>
              </Button>
            )}
          </div>
        </DialogHeader>
        <div className="flex justify-center overflow-auto bg-muted/40 p-4">
          {!url ? (
            <p className="py-10 text-sm text-muted-foreground">Geen voorbeeld beschikbaar.</p>
          ) : weergave === "mobiel" ? (
            <div className="rounded-[2.2rem] border-[10px] border-ink bg-ink shadow-2xl">
              <iframe key="m" title="Mobiel voorbeeld" src={url} className="block h-[72vh] w-[390px] rounded-[1.6rem] bg-background" data-testid="voorbeeld-iframe-mobiel" />
            </div>
          ) : (
            <iframe key="d" title="Desktop voorbeeld" src={url} className="block h-[72vh] w-full rounded-lg border bg-background" data-testid="voorbeeld-iframe-desktop" />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
